import { useEffect, useState } from "react"
import { useLocation, useParams } from "wouter"
import { useTranslation } from "react-i18next"
import { Link } from "wouter"
import { api, ApiRequestError } from "@/lib/api"
import { useAdminAuthStore } from "@/store/adminAuthStore"
import type { InvitationInfo } from "@fohet/caravan-booker-shared/types"

export const JoinPage = () => {
    const { t } = useTranslation()
    const { token } = useParams<{ token: string }>()
    const [, navigate] = useLocation()
    const user = useAdminAuthStore((s) => s.user)

    const [invite, setInvite] = useState<InvitationInfo | null>(null)
    const [loading, setLoading] = useState(true)
    const [notFound, setNotFound] = useState(false)
    const [accepting, setAccepting] = useState(false)
    const [accepted, setAccepted] = useState(false)

    useEffect(() => {
        api.get<InvitationInfo>(`/invitations/${token}`)
            .then(setInvite)
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false))
    }, [token])

    useEffect(() => {
        if (!invite || !user || accepting || accepted) return
        setAccepting(true)
        api.post(`/invitations/${token}/accept`, {})
            .then(() => {
                setAccepted(true)
                setTimeout(() => navigate("~/admin"), 2000)
            })
            .catch(() => {
                // Already a member or invite gone — still redirect to admin
                navigate("~/admin")
            })
    }, [invite, user, token, navigate, accepting, accepted])

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <p className="text-slate-500 font-bold">{t("join.loading")}</p>
            </div>
        )
    }

    if (notFound || !invite) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-10 w-full max-w-md text-center">
                    <h1 className="text-2xl font-black text-slate-900 mb-3">{t("join.errorTitle")}</h1>
                    <p className="text-slate-500 font-bold">{t("join.notFound")}</p>
                </div>
            </div>
        )
    }

    if (accepted) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-10 w-full max-w-md text-center">
                    <h1 className="text-2xl font-black text-slate-900 mb-3">{t("join.successTitle")}</h1>
                    <p className="text-slate-500 font-bold">{t("join.successMessage", { camp: invite.arealName })}</p>
                </div>
            </div>
        )
    }

    if (user && accepting) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <p className="text-slate-500 font-bold">{t("join.accepting")}</p>
            </div>
        )
    }

    const roleLabel = invite.role === "owner" ? t("join.roleOwner") : t("join.roleManager")
    const expiryDate = new Date(invite.expiresAt).toLocaleDateString()
    const registerUrl = `/admin/register?invite=${encodeURIComponent(token)}&email=${encodeURIComponent(invite.inviteeEmail)}`
    const loginUrl = `/admin/login?invite=${encodeURIComponent(token)}`

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-10 w-full max-w-md">
                <div className="mb-8 text-center">
                    <h1 className="text-3xl font-black tracking-tight text-slate-900 mb-2">{t("join.title")}</h1>
                    <p className="text-slate-600 font-bold mt-3">
                        {t("join.invitedBy", { name: invite.inviterName, camp: invite.arealName, role: roleLabel })}
                    </p>
                    <p className="text-slate-400 text-sm mt-2">{t("join.expires", { date: expiryDate })}</p>
                </div>

                <div className="space-y-3">
                    <Link
                        to={loginUrl}
                        className="flex items-center justify-center w-full px-6 py-3 rounded-[28px] bg-brand text-white font-black shadow-xl hover:bg-brand-dark active:scale-95 transition-all duration-200"
                    >
                        {t("join.loginToAccept")}
                    </Link>
                    <Link
                        to={registerUrl}
                        className="flex items-center justify-center w-full px-6 py-3 rounded-[28px] bg-charcoal text-white font-black shadow-xl hover:bg-slate-700 active:scale-95 transition-all duration-200"
                    >
                        {t("join.registerToAccept")}
                    </Link>
                </div>
            </div>
        </div>
    )
}
